import { type Node, nodeCount } from "@/lib/tool-kit/calculus/ast";
import {
  type DiffStep,
  derivativesUpTo,
  differentiate,
} from "@/lib/tool-kit/calculus/differentiate";
import { type ExprError, parseExpression } from "@/lib/tool-kit/calculus/parse";

/** Everything the derivative calculator shows, computed from the text the reader typed. */
export interface DerivativeReport {
  ast: Node;
  /** The variable differentiated with respect to. */
  variable: string;
  /** Every variable in the expression, in order of appearance. */
  variables: string[];
  /** True when the reader typed a decimal point, so results print as decimals. */
  decimals: boolean;
  /** Derivatives of order 1, 2, … (fewer than asked if they grew too large). */
  derivatives: Node[];
  /** The working for the first derivative. */
  steps: DiffStep[];
  truncated: boolean;
}

export type ReportResult =
  | { ok: true; report: DerivativeReport }
  | { ok: false; error: ExprError }
  | { ok: false; tooComplex: true };

const MAX_INPUT_NODES = 500;

/** The variable to use: the reader's choice if present, else x, else the first one. */
export function pickVariable(chosen: string, variables: string[]): string {
  if (variables.includes(chosen)) {
    return chosen;
  }
  return variables.includes("x") ? "x" : (variables[0] ?? "x");
}

export function buildDerivativeReport(
  text: string,
  chosen: string,
  order: number
): ReportResult {
  const parsed = parseExpression(text);
  if (!parsed.ok) {
    return { ok: false, error: parsed.error };
  }
  if (nodeCount(parsed.ast) > MAX_INPUT_NODES) {
    return { ok: false, tooComplex: true };
  }
  const variable = pickVariable(chosen, parsed.variables);
  const derivatives = derivativesUpTo(parsed.ast, variable, order);
  return {
    ok: true,
    report: {
      ast: parsed.ast,
      variable,
      variables: parsed.variables,
      decimals: parsed.usesDecimals,
      derivatives,
      steps: differentiate(parsed.ast, variable).steps,
      truncated: derivatives.length < order,
    },
  };
}

/** Does the expression contain an absolute value (whose derivative is undefined at 0)? */
export function containsAbs(node: Node): boolean {
  switch (node.kind) {
    case "call":
      return node.fn === "abs" || containsAbs(node.arg);
    case "add":
      return node.terms.some(containsAbs);
    case "mul":
      return node.factors.some(containsAbs);
    case "pow":
      return containsAbs(node.base) || containsAbs(node.exp);
    default:
      return false;
  }
}
